/**
 * Standalone TUS upload utility (no React dependency).
 *
 * Uses Supabase Storage's native TUS endpoint for resumable uploads.
 * Can be called from any context — React components, utility modules, etc.
 *
 * @see https://supabase.com/docs/guides/storage/uploads/resumable-uploads
 */
import * as tus from 'tus-js-client';
import { supabase } from './supabase';

/** Supabase requires exactly 6MB chunks for TUS uploads. */
const CHUNK_SIZE = 6 * 1024 * 1024;

/** Minimum file size to use TUS (6MB). Below this, standard upload is faster. */
export const TUS_THRESHOLD_BYTES = 6 * 1024 * 1024;

export interface TusProgressInfo {
  /** 0–100 */
  progress: number;
  bytesUploaded: number;
  bytesTotal: number;
  /** bytes/sec rolling average */
  bytesPerSecond: number;
}

export interface TusUploadOptions {
  /** Supabase Storage bucket name */
  bucket: string;
  /** Object path within the bucket */
  objectPath: string;
  /** MIME type of the file */
  contentType: string;
  /** Whether to overwrite existing files (default: true) */
  upsert?: boolean;
  /** Cache-Control header value (default: '3600') */
  cacheControl?: string;
  /** Progress callback — called on each chunk completion */
  onProgress?: (info: TusProgressInfo) => void;
}

/**
 * Derive the TUS endpoint URL from the Supabase project URL.
 */
function getTusEndpoint(): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  return `${supabaseUrl.replace(/\/$/, '')}/storage/v1/upload/resumable`;
}

/**
 * Upload a Blob/File to Supabase Storage via the TUS resumable upload protocol.
 * Resolves with the object path on success.
 *
 * For blobs < 6MB, prefer standard `supabase.storage.upload()` instead
 * (TUS has no benefit for small files).
 */
export async function uploadWithTus(
  fileOrBlob: File | Blob,
  options: TusUploadOptions
): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Not authenticated — cannot start TUS upload');
  }

  const endpoint = getTusEndpoint();

  return new Promise<string>((resolve, reject) => {
    const startTime = Date.now();

    const upload = new tus.Upload(fileOrBlob, {
      endpoint,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      chunkSize: CHUNK_SIZE,
      removeFingerprintOnSuccess: true,

      headers: {
        authorization: `Bearer ${session.access_token}`,
        'x-upsert': options.upsert !== false ? 'true' : 'false',
      },

      metadata: {
        bucketName: options.bucket,
        objectName: options.objectPath,
        contentType: options.contentType,
        cacheControl: options.cacheControl ?? '3600',
      },

      onProgress: (bytesUploaded: number, bytesTotal: number) => {
        const elapsed = (Date.now() - startTime) / 1000;
        const speed = elapsed > 0 ? bytesUploaded / elapsed : 0;
        const progress = Math.round((bytesUploaded / bytesTotal) * 100);

        options.onProgress?.({
          progress,
          bytesUploaded,
          bytesTotal,
          bytesPerSecond: speed,
        });
      },

      onSuccess: () => {
        resolve(options.objectPath);
      },

      onError: (err: Error | tus.DetailedError) => {
        reject(new Error(err.message || 'TUS upload failed'));
      },
    });

    // Check for previous incomplete uploads (crash recovery)
    upload.findPreviousUploads().then((previousUploads) => {
      if (previousUploads.length > 0) {
        console.log(
          `[TUS] Resuming previous upload (${previousUploads.length} found)`
        );
        upload.resumeFromPreviousUpload(previousUploads[0]!);
      }
      upload.start();
    });
  });
}

/**
 * Returns true if a file/blob is large enough to benefit from TUS.
 */
export function shouldUseTus(size: number): boolean {
  return size >= TUS_THRESHOLD_BYTES;
}
